import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search, RefreshCw, Filter, X, FilterX, Tag, ChevronDown,
  Calendar, Database, ShoppingCart,
  DollarSign, ChevronRight, Banknote,
  Loader2, ListPlus, List, Package, Truck, Clock, AlertCircle, CheckCheck, MessageSquareText,
  ArrowUpDown, ArrowUp, ArrowDown, Download
} from 'lucide-react';
import { useQuery } from '../QueryContext';
import { useAuth } from '../AuthContext';
import GlobalSearch from '../components/GlobalSearch';
import PedidoIndividual from '../components/PedidoIndividual';
import './DashboardBultos.css';
import './Pedidos.css';

/* ---------- FORMATOS ---------- */
const formatNum = (num) => new Intl.NumberFormat('de-DE').format(num || 0);
const formatUSD = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num || 0);
const formatBs = (num) => new Intl.NumberFormat('es-VE', { style: 'currency', currency: 'VES' }).format(num || 0);

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('es-ES', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
const PAGE_SIZE = isMobile ? 50 : 1000;

/* ---------- HELPER ROBUSTO: ¿el item está en SIN_STOCK? ---------- */
const isItemSinStock = (item) => {
  const raw = String(item?.estado_linea ?? '').trim();
  if (!raw) return false;
  const norm = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[\s_-]+/g, ' ')
    .trim();
  return norm === 'SIN STOCK' || norm === 'SINSTOCK' || norm.includes('SIN STOCK') || norm.includes('SINSTOCK');
};

/* ---------- OPCIONES FILTRO STOCK ---------- */
const STOCK_FILTER_OPTIONS = [
  { value: 'todos', label: 'Todos' },
  { value: 'solo_sin_stock', label: 'Solo Sin Stock' },
  { value: 'excluir_sin_stock', label: 'Excluir Sin Stock' },
];

/* ---------- EXPORTAR A EXCEL CON TODOS LOS RENGLONES ---------- */
const buildCSVRow = (arr) => arr.map(v => {
  if (v === null || v === undefined) return '""';
  const s = String(v).replace(/;/g, ',').replace(/\r?\n/g, ' ');
  return `"${s.replace(/"/g, '""')}"`;
}).join(';');

const exportarAExcel = async (pedidos, detallePedidos, stockFilter, isItemSinStock, setIsExporting) => {
  if (!pedidos || pedidos.length === 0) {
    alert('No hay pedidos para exportar.');
    return;
  }

  setIsExporting(true);
  try {
    const BOM = '\uFEFF';
    const sep = ';';

    const headers = [
      // Pedido
      'N° Pedido', 'Fecha Pedido', 'Cliente', 'ID Cliente', 'Vendedor', 'Moneda',
      'Estado Pedido', 'Fecha Despacho Pedido', 'Observaciones',
      'Total Cotizado Pedido', 'Total Procesado Pedido',
      'Items Cotizados Pedido', 'Items Procesados Pedido', 'Desc. Cliente %',
      // Renglón
      'SKU', 'Nombre Producto', 'Marca', 'UNI Venta', 'Estado Línea',
      'Bultos Cotizados', 'Bultos Facturados', 'Bultos Pendientes',
      'Total Cotizado Renglón', 'Total Procesado Renglón',
      'Desc. Propuesta %', 'Desc. Final %', 'Desc. PAC %',
      'Factura Real', 'Nota Entrega', 'Fecha Despacho Renglón'
    ];

    let csv = BOM + headers.join(sep) + '\n';
    let totalRenglones = 0;
    let pedidosSinDetalle = 0;

    pedidos.forEach(p => {
      const detalle = detallePedidos[p.fact_num];
      const items = detalle?.items || [];

      const datosPedido = [
        p.fact_num || '',
        p.fecha || '',
        p.cliente_nombre || '',
        p.cliente_id || '',
        p.vendedor_nombre || '',
        p.moneda || '',
        p.estado_procesamiento || '',
        p.fecha_despacho || '',
        (p.observaciones || '').replace(/;/g, ',').replace(/\n/g, ' '),
        (p.total || 0).toString().replace('.', ','),
        (p.total_procesado || 0).toString().replace('.', ','),
        p.items_cotizados || 0,
        p.items_procesados || 0,
        p.descuento_cliente || 0,
      ];

      if (items.length === 0) {
        pedidosSinDetalle++;
        csv += buildCSVRow([
          ...datosPedido,
          '(SIN DETALLE)', '', '', '', '', '', '', '',
          '', '', '', '', '', '', '', '', '', ''
        ]) + '\n';
        return;
      }

      items.forEach(item => {
        if (stockFilter === 'excluir_sin_stock' && isItemSinStock(item)) return;

        const totalCotizadoItem = p.moneda === 'USD' && item.reng_neto_usd != null
          ? item.reng_neto_usd
          : (item.reng_neto_cotizado || item.reng_neto || 0);
        const totalProcesadoItem = p.moneda === 'USD'
          ? (item.reng_neto_procesado_usd || 0)
          : (item.reng_neto_procesado || 0);

        csv += buildCSVRow([
          ...datosPedido,
          item.sku || '',
          item.nombre_producto || '',
          item.marca || '',
          item.uni_venta || '',
          item.estado_linea || '',
          (item.bultos_cotizados || 0).toString().replace('.', ','),
          (item.bultos_facturados || 0).toString().replace('.', ','),
          (item.bultos_pendientes_entrega || 0).toString().replace('.', ','),
          totalCotizadoItem.toString().replace('.', ','),
          totalProcesadoItem.toString().replace('.', ','),
          item.descuento_propuesta || 0,
          item.descuento_final || 0,
          item.descuento_pac || 0,
          item.factura_real || '',
          item.nota_entrega || '',
          item.fecha_despacho || '',
        ]) + '\n';
        totalRenglones++;
      });
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `pedidos_detallados_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log(`✅ Exportados ${totalRenglones} renglones de ${pedidos.length} pedidos`);
    if (pedidosSinDetalle > 0) {
      console.warn(`⚠️ ${pedidosSinDetalle} pedidos sin detalle cargado`);
    }
  } catch (e) {
    console.error('Error exportando:', e);
    alert('Error al exportar: ' + e.message);
  } finally {
    setIsExporting(false);
  }
};

/* ---------- MULTISELECT FILTER ---------- */
const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const hasSelection = selectedValues?.length > 0;
  const allOptionsSelected = options.length > 0 && selectedValues.length === options.length;

  const displayValue = allOptionsSelected
    ? 'Todos'
    : hasSelection
      ? (selectedValues.length === 1
        ? options.find(opt => (opt.value || opt) === selectedValues[0])?.label || selectedValues[0]
        : `${selectedValues.length} Selec.`)
      : 'Todos';

  const showSearch = options.length > 12;
  const filteredOptions = options
    .filter(opt => String(opt.label || opt).toLowerCase().includes(searchTerm.toLowerCase()))
    .slice(0, 100);

  const areAllSelected = filteredOptions.every(opt => selectedValues.includes(opt.value || opt));

  const handleSelectAll = () => {
    if (areAllSelected) {
      const newSelected = selectedValues.filter(
        val => !filteredOptions.some(opt => (opt.value || opt) === val)
      );
      onChange(name, newSelected);
    } else {
      const newValuesOnly = filteredOptions.map(opt => opt.value || opt);
      onChange(name, newValuesOnly);
    }
  };

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button className={`filter-pill-button ${hasSelection ? 'active' : ''}`} onClick={() => setIsOpen(!isOpen)}>
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{displayValue}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className={`filter-dropdown-panel ${showSearch ? 'with-search' : ''}`}>
          {showSearch && (
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input type="text" placeholder="Buscar..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} autoFocus />
            </div>
          )}
          {filteredOptions.length > 0 && (
            <label className="checkbox-row select-all-row">
              <input type="checkbox" checked={areAllSelected} onChange={handleSelectAll} />
              <span>Seleccionar Todos ({filteredOptions.length})</span>
            </label>
          )}
          <div className="options-list">
            {filteredOptions.map((opt, i) => {
              const optValue = opt.value || opt;
              const optLabel = opt.label || opt;
              return (
                <label key={`${name}-${optValue}-${i}`} className="checkbox-row">
                  <input type="checkbox" checked={selectedValues.includes(optValue)}
                    onChange={() => {
                      const newVals = selectedValues.includes(optValue)
                        ? selectedValues.filter(v => v !== optValue)
                        : [...selectedValues, optValue];
                      onChange(name, newVals);
                    }} />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
          {hasSelection && (
            <button className="reset-pill-link" onClick={() => onChange(name, [])}>
              Limpiar ({selectedValues.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const SingleSelectFilter = ({ label, value, options = [], onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => String(opt.value) === String(value)) || options[0] || { value, label: value };

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button type="button" className={`filter-pill-button active`} onClick={() => setIsOpen(!isOpen)}>
        <Tag size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{selectedOption.label || selectedOption.value}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className="filter-dropdown-panel dropdown-open-left">
          <div className="options-list">
            {options.map((opt) => (
              <label key={opt.value} className="checkbox-row">
                <input type="radio" name={`single-select-${label}`}
                  checked={String(value) === String(opt.value)}
                  onChange={() => { onChange(opt.value); setIsOpen(false); }} />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------- TARJETA DE MÉTRICA ---------- */
const MetricCard = React.memo(({ label, value, icon }) => (
  <div className="indicator-card2 v-real">
    <div className="card-content">
      <div className="inven-icon-wrapper">{icon}</div>
      <span className="indicator-label">{label}</span>
      <div className="indicator-right-group">
        <span className="indicator-value">{value}</span>
      </div>
    </div>
  </div>
));

/* ---------- FILTROS DE PEDIDO ---------- */
const filterLabelsPedido = {
  PED_CO_CLI: "Cliente",
  PED_CO_VEN: "Vendedor",
  PED_STATUS: "Estado",
  PED_NOMBRE: "Nombre Cliente",
  PED_NUMERO: "N° Pedido"
};

const INITIAL_PEDIDO_FILTERS = {
  PED_CO_CLI: [], PED_CO_VEN: [], PED_STATUS: [], PED_NOMBRE: [], PED_NUMERO: [], SEARCH: []
};

/* ---------- COMPONENTE PRINCIPAL ---------- */
const Pedidos = () => {
  const { getPedidosCatalogos, getMisPedidos, getPedidosFilters, getDetallePedido, updatePedidoEstado, invalidateConfigCache } = useQuery();
  const { profile } = useAuth();

  const [showOrderPanel, setShowOrderPanel] = useState(false);
  const [catalogData, setCatalogData] = useState(null);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
  const [isQuerying, setIsQuerying] = useState(false);
  const [pedidos, setPedidos] = useState([]);
  const [availableOptions, setAvailableOptions] = useState({});
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [sidebarFilters, setSidebarFilters] = useState(INITIAL_PEDIDO_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalBsGlobal, setTotalBsGlobal] = useState(0);
  const [totalUsdGlobal, setTotalUsdGlobal] = useState(0);
  const [totalUsdProcesadoGlobal, setTotalUsdProcesadoGlobal] = useState(0);
  const [expandedRows, setExpandedRows] = useState({});
  const [detallePedidos, setDetallePedidos] = useState({});
  const [loadingRows, setLoadingRows] = useState({});
  const [quickStatus, setQuickStatus] = useState(null);
  const [filtersReady, setFiltersReady] = useState(false);
  const [statusDrafts, setStatusDrafts] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'fecha', direction: 'desc' });
  const [stockFilter, setStockFilter] = useState('todos');
  const [isExporting, setIsExporting] = useState(false);
  const tableContainerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const navigate = useNavigate();

  // Refs espejo para evitar loops infinitos
  const detallePedidosRef = useRef({});
  const inFlightRef = useRef(new Set());
  useEffect(() => { detallePedidosRef.current = detallePedidos; }, [detallePedidos]);

  const today = useMemo(() => new Date(), []);
  const currentYear = useMemo(() => today.getFullYear(), [today]);
  const nombreMesActual = useMemo(() => today.toLocaleString('es-ES', { month: 'long' }).toUpperCase(), [today]);
  const [selectedYear, setSelectedYear] = useState([currentYear]);
  const [selectedMonth, setSelectedMonth] = useState([nombreMesActual]);
  const [selectedDay, setSelectedDay] = useState([today.getDate()]);
  const meses = useMemo(() => ["ENERO","FEBRERO","MARZO","ABRIL","MAYO","JUNIO","JULIO","AGOSTO","SEPTIEMBRE","OCTUBRE","NOVIEMBRE","DICIEMBRE"], []);
  const days = useMemo(() => Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: String(i + 1) })), []);
  const aniosOptions = useMemo(() => [
    currentYear - 2,
    currentYear - 1,
    currentYear,
  ].map(y => ({ value: y, label: String(y) })), [currentYear]);

  const isAsesor = useMemo(() => {
    const cargos = ["ASESOR COMERCIAL DE VENTAS", "ASESOR COMERCIAL JUNIOR","EJECUTIVO DE NEGOCIOS CUENTA CLAVE"];
    return profile?.cargo && cargos.includes(profile.cargo.toUpperCase());
  }, [profile]);
  const username = profile?.username || '';

  const loadCatalog = useCallback(async (force = false) => {
    setIsLoadingCatalog(true);
    try {
      if (force && invalidateConfigCache) {
        invalidateConfigCache('/pedidosapp');
      }
      const data = await getPedidosCatalogos(force);
      if (data?.success) setCatalogData(data);
    }
    catch (e) { console.error('Error catálogo:', e); }
    finally { setIsLoadingCatalog(false); }
  }, [getPedidosCatalogos, invalidateConfigCache]);

  const normalizeEstadoFiltro = useCallback((value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim().toUpperCase();
    if (['0', 'SIN PROCESAR', 'SIN_PROCESAR', 'PENDIENTE', 'PENDING'].includes(normalized)) return 'SIN PROCESAR';
    if (['1', 'FACTURADA', 'COMPLETO'].includes(normalized)) return 'FACTURADA';
    if (['PARCIAL'].includes(normalized)) return 'PARCIAL';
    if (['PARCIAL - DESPACHADO'].includes(normalized)) return 'PARCIAL - DESPACHADO';
    if (['EN NOTA', 'EN_NOTA', 'NOTA'].includes(normalized)) return 'EN NOTA';
    if (['DESPACHADO', 'DESPACHADA'].includes(normalized)) return 'DESPACHADO';
    if (['RECHAZADO'].includes(normalized)) return 'RECHAZADO';
    if (['R. FACT ANULADA', 'R_FACT_ANULADA', 'FACT ANULADA'].includes(normalized)) return 'R. FACT ANULADA';
    if (['RETENIDO POR COBRO', 'RETENIDO_POR_COBRO', 'RETENIDO'].includes(normalized)) return 'RETENIDO POR COBRO';
    return normalized || null;
  }, []);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  const sortPedidos = useCallback((pedidosList) => {
    if (!sortConfig.key) return pedidosList;
    return [...pedidosList].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      if (sortConfig.key === 'fecha' || sortConfig.key === 'fecha_despacho') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      } else if (['total','total_procesado','items_cotizados','items_procesados'].includes(sortConfig.key)) {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      } else {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortConfig]);

  const handleSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  }, []);

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown size={12} style={{ opacity: 0.4 }} />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp size={12} style={{ opacity: 1 }} />
      : <ArrowDown size={12} style={{ opacity: 1 }} />;
  };

  const buildFilterParams = useCallback(() => {
    const params = {};
    const statusFiltro = normalizeEstadoFiltro(quickStatus);
    if (statusFiltro) params.status = statusFiltro;
    if (isAsesor && username) params.usuario = username;
    if (sidebarFilters.PED_CO_CLI?.length) params.cliente = sidebarFilters.PED_CO_CLI.join(',');
    if (sidebarFilters.PED_CO_VEN?.length) params.vendedor = sidebarFilters.PED_CO_VEN.join(',');
    if (sidebarFilters.PED_STATUS?.length) params.status_list = sidebarFilters.PED_STATUS.join(',');
    if (sidebarFilters.PED_NOMBRE?.length) params.nombre_cliente = sidebarFilters.PED_NOMBRE[0];
    if (sidebarFilters.PED_NUMERO?.length) {
      const numerosValidos = sidebarFilters.PED_NUMERO.map(v => String(v).trim()).filter(v => v && !isNaN(v));
      if (numerosValidos.length > 0) params.fact_num = numerosValidos.join(',');
    }
    if (sidebarFilters.SEARCH?.length) params.buscar = sidebarFilters.SEARCH[0];

    if (selectedYear.length === 1 && selectedMonth.length === 1) {
      const year = selectedYear[0];
      const monthIndex = meses.indexOf(selectedMonth[0]) + 1;
      if (monthIndex > 0) {
        params.fec_desde = `${year}-${String(monthIndex).padStart(2, '0')}-01`;
        const lastDay = new Date(year, monthIndex, 0).getDate();
        params.fec_hasta = `${year}-${String(monthIndex).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      }
    }
    if (selectedDay.length > 0 && selectedDay.length < 31) {
      params.dias = selectedDay.join(',');
    }
    return params;
  }, [quickStatus, isAsesor, username, sidebarFilters, selectedYear, selectedMonth, selectedDay, meses, normalizeEstadoFiltro]);

  const fetchData = useCallback(async (page = 1) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setIsQuerying(true);
    try {
      const params = buildFilterParams();
      params.page = page;
      params.page_size = PAGE_SIZE;

      const result = await getMisPedidos(params);
      if (signal.aborted) return;

      if (result?.success) {
        const data = Array.isArray(result.data) ? result.data : [];
        setPedidos(data);
        setCurrentPage(page);
        if (result.total_count !== undefined) setTotalCount(Number(result.total_count ?? 0));
        if (result.total_bs !== undefined) setTotalBsGlobal(Number(result.total_bs ?? 0));
        if (result.total_usd !== undefined) setTotalUsdGlobal(Number(result.total_usd ?? 0));
        if (result.total_usd_procesado !== undefined) setTotalUsdProcesadoGlobal(Number(result.total_usd_procesado ?? 0));
      }
    } catch (e) {
      if (e.name !== 'AbortError') console.error('Error:', e);
    } finally {
      if (!signal.aborted) setIsQuerying(false);
    }
  }, [buildFilterParams, getMisPedidos]);

  // loadDetallePedido estable (usa refs)
  const loadDetallePedido = useCallback(async (factNum) => {
    if (detallePedidosRef.current[factNum]) return;
    if (inFlightRef.current.has(factNum)) return;
    inFlightRef.current.add(factNum);

    setLoadingRows(prev => ({ ...prev, [factNum]: true }));
    try {
      const payload = await getDetallePedido(factNum);
      if (!payload || !payload.success) return;
      const items = payload.items || [];
      setDetallePedidos(prev => ({
        ...prev,
        [factNum]: {
          items,
          items_total: items.length,
          items_bultos_facturados: payload.items_bultos_facturados || 0,
          items_procesados_lines: payload.items_procesados_lines || 0,
          pedido: payload.pedido || {}
        }
      }));
    } catch (err) {
      console.warn(`Detalle no disponible: ${factNum}`);
    } finally {
      inFlightRef.current.delete(factNum);
      setLoadingRows(prev => ({ ...prev, [factNum]: false }));
    }
  }, [getDetallePedido]);

  const getDateRangeParams = useCallback(() => {
    const params = {};
    if (selectedYear.length === 1 && selectedMonth.length === 1) {
      const year = selectedYear[0];
      const monthIndex = meses.indexOf(selectedMonth[0]) + 1;
      if (monthIndex > 0) {
        params.fec_desde = `${year}-${String(monthIndex).padStart(2, '0')}-01`;
        const lastDay = new Date(year, monthIndex, 0).getDate();
        params.fec_hasta = `${year}-${String(monthIndex).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      }
    }
    return params;
  }, [selectedYear, selectedMonth, meses]);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const dateParams = getDateRangeParams();
        const conf = await getPedidosFilters(dateParams);
        if (conf?.success) {
          setAvailableOptions(conf.availableOptions || {});
          setFiltersReady(true);
        }
      } catch (e) { console.error('Error filters:', e); }
    };
    loadFilters();
  }, [getPedidosFilters, getDateRangeParams]);

  useEffect(() => {
    if (!filtersReady) return;
    fetchData(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReady, selectedYear, selectedMonth, selectedDay, quickStatus, sidebarFilters, currentPage]);

  const handleRefresh = useCallback(() => {
    setDetallePedidos({});
    setExpandedRows({});
    fetchData(currentPage);
    loadCatalog(true);
    const dateParams = getDateRangeParams();
    getPedidosFilters(dateParams)
      .then(conf => {
        if (conf?.success && conf.availableOptions) {
          setAvailableOptions(conf.availableOptions);
        }
      })
      .catch(e => console.warn('Error refreshing filters:', e));
  }, [currentPage, fetchData, getPedidosFilters, getDateRangeParams, loadCatalog]);

  const handleResetFilters = () => {
    setSidebarFilters(INITIAL_PEDIDO_FILTERS);
    setQuickStatus(null);
    setStockFilter('todos');
  };

  const totalPages = useMemo(() => Math.ceil(totalCount / PAGE_SIZE) || 1, [totalCount]);

  const sortedPedidos = useMemo(() => sortPedidos(pedidos), [pedidos, sortPedidos]);

  const filteredPedidos = useMemo(() => {
    if (stockFilter === 'todos') return sortedPedidos;
    return sortedPedidos.filter(p => {
      const detalle = detallePedidos[p.fact_num];
      const tieneSinStock = (detalle?.items && detalle.items.length > 0)
        ? detalle.items.some(isItemSinStock)
        : Boolean(p.tiene_sin_stock);

      if (stockFilter === 'solo_sin_stock') return tieneSinStock;
      if (stockFilter === 'excluir_sin_stock') return !tieneSinStock;
      return true;
    });
  }, [sortedPedidos, stockFilter, detallePedidos]);

  const displayTotalCount = useMemo(() => (
    stockFilter === 'todos' ? totalCount : filteredPedidos.length
  ), [stockFilter, totalCount, filteredPedidos]);

  const displayTotalBs = useMemo(() => (
    stockFilter === 'todos'
      ? totalBsGlobal
      : filteredPedidos.reduce((acc, p) => acc + (p.moneda === 'BS' ? (p.total || 0) : 0), 0)
  ), [stockFilter, totalBsGlobal, filteredPedidos]);

  const displayTotalUSD = useMemo(() => (
    stockFilter === 'todos'
      ? totalUsdGlobal
      : filteredPedidos.reduce((acc, p) => acc + (p.moneda === 'USD' ? (p.total || 0) : 0), 0)
  ), [stockFilter, totalUsdGlobal, filteredPedidos]);

  const displayTotalUSDProcesado = useMemo(() => (
    stockFilter === 'todos'
      ? totalUsdProcesadoGlobal
      : filteredPedidos.reduce((acc, p) => acc + (p.total_procesado || 0), 0)
  ), [stockFilter, totalUsdProcesadoGlobal, filteredPedidos]);

  const togglePedido = useCallback((factNum) => {
    setExpandedRows(prev => {
      const willExpand = !prev[factNum];
      if (willExpand) loadDetallePedido(factNum);
      return { ...prev, [factNum]: willExpand };
    });
  }, [loadDetallePedido]);

  /* ---------- EXPORTACIÓN CON CARGA PREVIA DE DETALLES ---------- */
  const cargarDetallesFaltantes = useCallback(async (pedidosList) => {
    const faltantes = pedidosList
      .map(p => p.fact_num)
      .filter(fn => fn && !detallePedidosRef.current[fn]);

    if (faltantes.length === 0) {
      return { ...detallePedidosRef.current };
    }

    console.log(`Cargando ${faltantes.length} detalles faltantes para exportar...`);
    const CONCURRENCY = 8;
    const resultados = { ...detallePedidosRef.current };

    for (let i = 0; i < faltantes.length; i += CONCURRENCY) {
      const batch = faltantes.slice(i, i + CONCURRENCY);
      await Promise.all(batch.map(async (fn) => {
        try {
          const payload = await getDetallePedido(fn);
          if (payload?.success) {
            const items = payload.items || [];
            resultados[fn] = {
              items,
              items_total: items.length,
              items_bultos_facturados: payload.items_bultos_facturados || 0,
              items_procesados_lines: payload.items_procesados_lines || 0,
              pedido: payload.pedido || {}
            };
          }
        } catch (err) {
          console.warn(`No se pudo cargar detalle de ${fn}:`, err);
        }
      }));
    }

    setDetallePedidos(resultados);
    return resultados;
  }, [getDetallePedido]);

  const handleExport = useCallback(async () => {
    if (filteredPedidos.length === 0) return;
    if (isExporting) return;

    const detallesCompletos = await cargarDetallesFaltantes(filteredPedidos);

    await exportarAExcel(
      filteredPedidos,
      detallesCompletos,
      stockFilter,
      isItemSinStock,
      setIsExporting
    );
  }, [filteredPedidos, cargarDetallesFaltantes, stockFilter, isExporting]);

  const estadoConfig = {
    'Facturada': { bg: 'rgb(0, 204, 99)', color: 'white', border: 'rgba(0,210,106,0.35)', Icon: CheckCheck },
    'Parcial': { bg: '#ffc021', color: 'white', border: 'rgba(251,191,36,0.35)', Icon: AlertCircle },
    'Parcial - Despachado': { bg: '#ffc021', color: 'white', border: 'rgba(251,191,36,0.35)', Icon: AlertCircle },
    'En Nota': { bg: 'rgba(59, 131, 246, 0.7)', color: '#3b82f6', border: 'rgba(59,130,246,0.35)', Icon: Package },
    'Sin Procesar': { bg: '#3b82f6', color: 'white', border: '#3b82f6', Icon: Clock },
    'Despachado': { bg: 'rgba(138, 92, 246, 0.6)', color: 'white', border: 'rgba(92, 105, 246, 0.35)', Icon: Truck },
    'Rechazado': { bg: '#8f0000', color: 'white', border: 'rgba(239,68,68,0.25)', Icon: X },
    'R. Fact Anulada': { bg: '#8f0000', color: 'white', border: 'rgba(239,68,68,0.25)', Icon: X },
    'Retenido por Cobro': { bg: 'rgba(7, 7, 6, 0.18)', color: '#ffc635', border: 'rgba(245,158,11,0.25)', Icon: AlertCircle },
  };

  const normalizeEstadoProcesamiento = (estado) => {
    const upper = String(estado || 'Sin Procesar').trim().toUpperCase();
    if (upper === 'FACTURADA' || upper === 'COMPLETO') return 'Facturada';
    if (upper === 'PARCIAL') return 'Parcial';
    if (upper === 'PARCIAL - DESPACHADO') return 'Parcial - Despachado';
    if (upper === 'EN NOTA' || upper === 'EN_NOTA' || upper === 'NOTA') return 'En Nota';
    if (upper === 'SIN PROCESAR' || upper === 'SIN_PROCESAR' || upper === 'PENDIENTE' || upper === 'PENDING') return 'Sin Procesar';
    if (upper === 'DESPACHADO' || upper === 'DESPACHADA') return 'Despachado';
    if (upper === 'RECHAZADO') return 'Rechazado';
    if (upper === 'R. FACT ANULADA' || upper === 'R_FACT_ANULADA' || upper === 'FACT ANULADA') return 'R. Fact Anulada';
    if (upper === 'RETENIDO POR COBRO' || upper === 'RETENIDO_POR_COBRO' || upper === 'RETENIDO') return 'Retenido por Cobro';
    return 'Sin Procesar';
  };

  const normalizeText = (s) =>
    String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();

  const canUpdatePedidoStatus = useCallback((pedidoEstado) => {
    const estado = normalizeEstadoProcesamiento(pedidoEstado);
    const estadoRaw = normalizeText(pedidoEstado);
    if (estado === 'Parcial' || estado === 'Parcial - Despachado') return false;
    if (estado === 'Facturada' || estado === 'Despachado') return false;
    if (estado === 'R. Fact Anulada' || estado === 'Rechazado') return false;
    if (estado === 'Retenido por Cobro') return false;
    const esPendiente = estado === 'Sin Procesar' || estadoRaw === 'SIN PROCESAR';
    const esEnNota = estado === 'En Nota' || estadoRaw === 'EN NOTA' || estadoRaw === 'EN_NOTA' || estadoRaw === 'NOTA';
    return esPendiente || esEnNota;
  }, []);

  const handlePedidoStatusChange = useCallback(async (factNum, nuevoEstado) => {
    if (!factNum || !nuevoEstado) return;
    try {
      const result = await updatePedidoEstado({
        fact_num: Number(factNum),
        nuevo_estado: nuevoEstado,
        cargo: profile?.cargo,
        username: profile?.username,
      });
      if (result?.success) {
        setStatusDrafts(prev => ({ ...prev, [factNum]: undefined }));
        fetchData(currentPage);
      }
    } catch (error) {
      console.error('Error actualizando estado del pedido:', error);
      alert(error.message || 'No se pudo actualizar el estado del pedido.');
    }
  }, [currentPage, fetchData, profile?.cargo, profile?.username, updatePedidoEstado]);

  const handleStatusDraftChange = useCallback((factNum, value) => {
    setStatusDrafts(prev => ({ ...prev, [factNum]: value }));
  }, []);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  return (
    <motion.div className="dashboard-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* HEADER */}
      <header className="header-flex">
        <div className="title-section">
          <h1 className="dashboard-title">Gestión de Pedidos</h1>
          <div className="title-underline"></div>
        </div>
        <div className="date-selectors">
          <button className="refresh-button" onClick={handleRefresh} disabled={isQuerying}>
            <RefreshCw size={16} className={isQuerying ? "spin-anim" : ""} />
          </button>
          <button className="new-order-btn" onClick={() => navigate('/pedidosmasivos')} disabled={isLoadingCatalog}>
            {isLoadingCatalog ? <Loader2 size={18} className="spin-anim" /> : <ShoppingCart size={18} />}
            <span style={{ marginLeft: '8px' }}>Nuevo Pedido</span>
          </button>
          <button
            className="new-order-btn"
            onClick={handleExport}
            disabled={filteredPedidos.length === 0 || isExporting}
            style={{
              background: '#1d4ed8',
              opacity: (filteredPedidos.length === 0 || isExporting) ? 0.6 : 1
            }}
            title="Exportar todos los pedidos con todos sus renglones"
          >
            {isExporting ? <Loader2 size={18} className="spin-anim" /> : <Download size={18} />}
            <span style={{ marginLeft: '8px' }}>
              {isExporting ? 'Exportando...' : 'Exportar'}
            </span>
          </button>
          <button className="mobile-filter-btn" onClick={() => setShowMobileFilters(true)}>
            <Filter size={16} /><span>Filtros</span>
          </button>
          <div className="date-filters-group">
            <MultiSelectFilter label="Día" name="dia" icon={Calendar} options={days} selectedValues={selectedDay} onChange={(_, vals) => setSelectedDay(vals)} />
            <MultiSelectFilter label="Mes" name="meses" icon={Calendar} options={meses} selectedValues={selectedMonth} onChange={(_, vals) => setSelectedMonth(vals)} />
            <MultiSelectFilter label="Año" name="anios" icon={Calendar} options={aniosOptions} selectedValues={selectedYear} onChange={(_, vals) => setSelectedYear(vals)} />
          </div>
          <div style={isMobile ? { width: '100%', marginTop: '8px' } : {}}>
            <GlobalSearch availableOptions={availableOptions} filterLabels={filterLabelsPedido} filters={sidebarFilters} setFilters={setSidebarFilters} />
          </div>
        </div>
      </header>

      {/* FILTROS LATERALES */}
      <div className={`filters-sidebar-container ${showMobileFilters ? 'open' : ''}`}>
        <div className="filters-sidebar-header">
          <h3>Filtros Disponibles</h3>
          <div className="filters-header-actions">
            <button className="reset-filters-btn" onClick={handleResetFilters}><FilterX size={16} /></button>
            <button className="close-filters-btn" onClick={() => setShowMobileFilters(false)}><X size={20} /></button>
          </div>
        </div>
        <div className="filters-grid" style={{ gap: '8px' }}>
          {Object.keys(filterLabelsPedido).map((key) => (
            <MultiSelectFilter key={key} label={filterLabelsPedido[key]} name={key} icon={Tag} options={availableOptions[key] || []} selectedValues={sidebarFilters[key] || []} onChange={(name, vals) => setSidebarFilters(prev => ({ ...prev, [name]: vals }))} />
          ))}
          <SingleSelectFilter
            label="Stock"
            value={stockFilter}
            options={STOCK_FILTER_OPTIONS}
            onChange={(val) => { setStockFilter(val); setCurrentPage(1); }}
          />
          <button className="filter-pill-button reset-filters" onClick={handleResetFilters}><FilterX size={12} /><span>Limpiar</span></button>
        </div>
      </div>
      {showMobileFilters && <div className="filters-overlay" onClick={() => setShowMobileFilters(false)} />}

      {/* TARJETAS KPI */}
      <div className="dashboard-indicators2" style={{ padding: '15px', background: 'transparent' }}>
        <MetricCard label="Total Pedidos" value={formatNum(displayTotalCount)} icon={<ListPlus size={22} color="white" />} />
        <MetricCard label="Total Bs" value={formatBs(displayTotalBs)} icon={<Banknote size={22} color="white" />} />
        <MetricCard label="Total USD Cotizado" value={formatUSD(displayTotalUSD)} icon={<DollarSign size={22} color="white" />} />
        <MetricCard label="Total USD Procesado" value={formatUSD(displayTotalUSDProcesado)} icon={<CheckCheck size={22} color="white" />} />
      </div>

      {/* BANNER FILTRO STOCK */}
      {stockFilter !== 'todos' && (
        <div style={{
          fontSize: 12, padding: '8px 12px', background: 'rgba(59,130,246,0.12)',
          borderRadius: 8, margin: '0 15px 5px', display: 'flex',
          alignItems: 'center', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Filter size={14} />
            <span>
              {stockFilter === 'solo_sin_stock'
                ? '🔎 Mostrando solo pedidos con líneas SIN_STOCK'
                : '🚫 Ocultando pedidos con líneas SIN_STOCK'}
            </span>
          </div>
          <span style={{ fontSize: 11, opacity: 0.7 }}>
            Visible: {formatNum(filteredPedidos.length)} de {formatNum(sortedPedidos.length)} en esta página
          </span>
        </div>
      )}

      {/* TABLA */}
      <div className="detail-table-container" style={{ marginTop: '20px', opacity: isQuerying ? 0.8 : 1, transition: 'opacity 0.3s' }}>
        <div className="table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'nowrap' }}>
          <h3 style={{ margin: 0, whiteSpace: 'nowrap' }}>Historial de Pedidos</h3>
          <div className="table-controls" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'nowrap' }}>
            <span style={{ whiteSpace: 'nowrap' }}>Total registros: {formatNum(totalCount)}</span>
            <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
              <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1 || isQuerying}>Anterior</button>
              <span>Página {currentPage} de {totalPages}</span>
              <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage >= totalPages || isQuerying}>Siguiente</button>
            </div>
          </div>
        </div>

        {filteredPedidos.length === 0 ? (
          <div style={{ padding: '50px', textAlign: 'center', opacity: 0.5 }}>
              {isQuerying ? 'Cargando pedidos...' : 'No hay pedidos que coincidan.'}
          </div>
        ) : (
          <>
            <div className="responsive-table-wrapper" ref={tableContainerRef} style={{ maxHeight: '60vh', overflow: 'auto' }}>
              <table className="detail-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('fecha')} style={{ cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>Fecha <SortIcon columnKey="fecha" /></div>
                    </th>
                    <th onClick={() => handleSort('fact_num')} style={{ cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>Pedido <SortIcon columnKey="fact_num" /></div>
                    </th>
                    <th onClick={() => handleSort('cliente_nombre')} style={{ cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>Cliente <SortIcon columnKey="cliente_nombre" /></div>
                    </th>
                    <th onClick={() => handleSort('total')} style={{ textAlign: 'right', cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>Total Cotizado <SortIcon columnKey="total" /></div>
                    </th>
                    <th onClick={() => handleSort('total_procesado')} style={{ textAlign: 'right', cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>Total Procesado <SortIcon columnKey="total_procesado" /></div>
                    </th>
                    <th style={{ textAlign: 'right' }}>Items Cot.</th>
                    <th style={{ textAlign: 'right' }}>Items Proc.</th>
                    <th>Moneda</th>
                    <th onClick={() => handleSort('vendedor_nombre')} style={{ cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>Vendedor <SortIcon columnKey="vendedor_nombre" /></div>
                    </th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPedidos.map((group) => {
                    const isExpanded = expandedRows[group.fact_num];
                    const isLoadingRow = loadingRows[group.fact_num];
                    const detalle = detallePedidos[group.fact_num] || {};
                    const items = detalle.items || [];

                    const itemsCotizados = group.items_cotizados || 0;
                    const itemsProcesados = group.items_procesados || 0;
                    const totalCotizado = group.total || 0;
                    const totalProcesado = group.total_procesado || 0;
                    const fechaDespacho = group.fecha_despacho;
                    const formatMoneda = group.moneda === 'BS' ? formatBs : formatUSD;
                    const descCliente = group.descuento_cliente || 0;

                    const facturas = group.facturas || [];
                    const facturasStr = facturas.map(f => `#${f.factura}`).join(', ');
                    const primeraNota = facturas.length > 0 ? facturas[0].nota : null;

                    const sinStockCount = items.filter(isItemSinStock).length;

                    const epRaw = (group.estado_procesamiento || 'PENDIENTE').trim();
                    const ep = normalizeEstadoProcesamiento(epRaw);
                    const ea = epRaw.includes('DESPACHADO') ? 'Despachado'
                      : (fechaDespacho && ep === 'Facturada' ? 'Despachado' : ep);

                    const config = estadoConfig[ea] || estadoConfig['Sin Procesar'];
                    const { Icon: StatusIcon = Clock } = config || {};
                    const puedeCambiarEstado = canUpdatePedidoStatus(group.estado_procesamiento);
                    const normalizar = (s) =>
                      String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();

                    const cargo = normalizar(profile?.cargo);
                    const esAsesor = 
                      cargo.includes('ASESOR COMERCIAL DE VENTAS') ||
                      cargo.includes('ASESOR COMERCIAL JUNIOR') ||
                      cargo.includes('EJECUTIVO DE NEGOCIOS CUENTA CLAVE');
                    const esAdmin =
                      cargo.includes('ESPECIALISTA DE ADMINISTRACION DE VENTAS') ||
                      cargo.includes('ESPECIALISTA DE ADMIN DE VENTAS') ||
                      cargo.includes('COORDINADOR DE INTELIGENCIA COMERCIAL') ||
                      cargo.includes('COORDINADOR DE INTELIGENCIA DE NEGOCIO');

                    const estadoActual = normalizar(group.estado_procesamiento) || 'SIN PROCESAR';
                    const opcionesPermitidas = esAsesor
                      ? ['Sin Procesar', 'Rechazado']
                      : esAdmin
                        ? ['Sin Procesar', 'Retenido Por Cobro', 'Rechazado']
                        : ['Sin Procesar', 'Rechazado'];

                    const draftStatus = statusDrafts[group.fact_num] ?? (estadoActual || 'Sin Procesar');

                    return (
                      <React.Fragment key={group.fact_num}>
                        <tr onClick={() => togglePedido(group.fact_num)} style={{ cursor: 'pointer', opacity: 1 }}>
                          <td>{formatDateTime(group.fecha)}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {isLoadingRow ? <Loader2 size={16} className="spin-anim" /> :
                                isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                              <span>{group.fact_num}</span>
                            </div>

                            {facturas.length > 0 && (
                              <div style={{ fontSize: '0.8rem', color: 'var(--in-table)', marginTop: '2px', fontWeight: 600 }}>
                                Fact: {facturasStr}
                                {primeraNota && <span> | Nota: #{primeraNota}</span>}
                              </div>
                            )}

                            {sinStockCount > 0 && (
                              <div style={{ marginTop: '3px' }}>
                                <span style={{
                                  fontSize: '0.65rem', background: 'rgba(239,68,68,0.18)',
                                  border: '1px solid rgba(239,68,68,0.5)', color: '#f87171',
                                  borderRadius: '4px', padding: '1px 6px', fontWeight: 700,
                                  display: 'inline-flex', alignItems: 'center', gap: 3
                                }}>
                                  <AlertCircle size={10} /> {sinStockCount} SIN_STOCK
                                </span>
                              </div>
                            )}

                            {fechaDespacho && (
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-third)', marginTop: '1px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Truck size={10} />
                                <span>Desp: {formatDateTime(fechaDespacho)}</span>
                              </div>
                            )}
                          </td>
                          <td>
                            {group.cliente_nombre}
                            <div style={{ fontSize: 11, opacity: 0.7 }}>ID: {group.cliente_id}</div>

                            {descCliente > 0 && (
                              <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                <span style={{ fontSize: '0.6rem', background: '#cd0000', border: '1px solid rgba(239,68,68,0.3)', color: 'white', borderRadius: '4px', padding: '1px 4px', fontWeight: 600 }}>
                                  Cli: {descCliente}%
                                </span>
                              </div>
                            )}

                            {group.observaciones && (
                              <div style={{ marginTop: '4px', maxWidth: '200px' }}>
                                <span title={group.observaciones} style={{ fontSize: '0.75rem', color: 'var(--text-third)', borderRadius: '4px', padding: '2px 6px', display: 'inline-block', whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: '100%', cursor: 'help', lineHeight: '1.4' }}>
                                  <MessageSquareText size={12} style={{ flexShrink: 0 }} />
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {group.observaciones}
                                  </span>
                                </span>
                              </div>
                            )}
                          </td>
                          <td style={{ textAlign: 'right' }}>{formatMoneda(totalCotizado)}</td>
                          <td style={{ textAlign: 'right' }}>{totalProcesado > 0 ? formatMoneda(totalProcesado) : '-'}</td>
                          <td style={{ textAlign: 'right' }}>{formatNum(itemsCotizados)}</td>
                          <td style={{ textAlign: 'right' }}>{formatNum(itemsProcesados)}</td>
                          <td>{group.moneda}</td>
                          <td>{group.vendedor_nombre || ''}</td>
                          <td>
                            {puedeCambiarEstado ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <SingleSelectFilter
                                  label="Estado"
                                  value={draftStatus}
                                  options={opcionesPermitidas.map((option) => ({ value: option, label: option }))}
                                  onChange={(nextValue) => handleStatusDraftChange(group.fact_num, nextValue)}
                                />
                                <button
                                  type="button"
                                  onClick={() => handlePedidoStatusChange(group.fact_num, draftStatus)}
                                  disabled={!draftStatus || draftStatus.toUpperCase() === estadoActual}
                                  style={{
                                    padding: '6px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)',
                                    background: draftStatus && draftStatus.toUpperCase() !== estadoActual ? '#1d4ed8' : 'rgba(255,255,255,0.08)',
                                    color: '#fff', cursor: draftStatus && draftStatus.toUpperCase() !== estadoActual ? 'pointer' : 'not-allowed', fontSize: 11, fontWeight: 700
                                  }}
                                >
                                  Enviar
                                </button>
                              </div>
                            ) : (
                              <span style={{ padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: config.bg, color: config.color, border: `1px solid ${config.border}`, display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                                <StatusIcon size={12} />{ea}
                              </span>
                            )}
                          </td>
                        </tr>

                        {isExpanded && (
                          <>
                            {isLoadingRow ? (
                              <tr>
                                <td colSpan={10} style={{ textAlign: 'center', padding: '20px' }}>
                                  <Loader2 size={24} className="spin-anim" /> Cargando detalles...
                                </td>
                              </tr>
                            ) : (
                              items.map((item, si) => {
                                const bultosCotizados = item.bultos_cotizados || 0;
                                const bultosFacturados = item.bultos_facturados || 0;
                                const pendienteEntrega = item.bultos_pendientes_entrega || 0;
                                const totalProcesadoItem = group.moneda === 'USD'
                                  ? (item.reng_neto_procesado_usd || 0)
                                  : (item.reng_neto_procesado || 0);
                                const totalCotizadoItem = group.moneda === 'USD' && item.reng_neto_usd != null
                                  ? item.reng_neto_usd
                                  : (item.reng_neto_cotizado || item.reng_neto || 0);
                                const fechaDespachoItem = item.fecha_despacho || null;
                                const facturaRealItem = item.factura_real || null;
                                const notaEntregaItem = item.nota_entrega || null;

                                const itemEsSinStock = isItemSinStock(item);
                                if (stockFilter === 'excluir_sin_stock' && itemEsSinStock) return null;

                                const estadoLineaNorm = String(item.estado_linea || '').toUpperCase();
                                const esCompleto = estadoLineaNorm === 'COMPLETO';

                                return (
                                  <tr key={`${group.fact_num}-${si}`} style={{ background: 'rgba(255,255,255,0.02)' }}>
                                    <td></td>
                                    <td style={{ fontSize: '0.8rem' }} colSpan={2}>
                                      <div style={{ fontWeight: 500 }}>{item.sku}</div>
                                      <div style={{ color: 'var(--sd-button-active)', fontSize: '0.7rem' }}>{item.nombre_producto}</div>

                                      {facturaRealItem && (
                                        <div style={{ fontSize: '0.7rem', color: 'var(--in-table)', marginTop: '2px', fontWeight: 600 }}>
                                          Fact: #{facturaRealItem}
                                          {notaEntregaItem && <span style={{ color: 'var(--in-table)' }}> | Nota: #{notaEntregaItem}</span>}
                                        </div>
                                      )}

                                      <div style={{ marginTop: '4px', fontSize: '0.7rem' }}>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                          <span style={{ color: 'var(--text-third)' }}>Cot: <strong style={{ color: '#fff' }}>{formatNum(bultosCotizados)}</strong></span>
                                          <span style={{ color: 'var(--text-third)' }}>Fact: <strong style={{ color: esCompleto ? '#00d26a' : '#ffb700' }}>{formatNum(bultosFacturados)}</strong></span>
                                          {pendienteEntrega > 0 && !esCompleto && bultosFacturados < bultosCotizados && (
                                            <span style={{ color: 'var(--text-third)' }}>Pend: <strong style={{ color: '#ff4d4f' }}>{formatNum(pendienteEntrega)}</strong></span>
                                          )}
                                        </div>
                                        {bultosCotizados > 0 && (
                                          <div style={{ width: '100%', height: '3px', background: 'rgba(255,255,255,0.1)', borderRadius: '2px', marginTop: '3px', overflow: 'hidden' }}>
                                            <div style={{ width: `${(bultosFacturados / bultosCotizados) * 100}%`, height: '100%', background: esCompleto ? '#00d26a' : '#ffb700', borderRadius: '2px' }} />
                                          </div>
                                        )}
                                      </div>

                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                                        {(item.descuento_propuesta || 0) > 0 && (
                                          <span style={{ fontSize: '0.6rem', background: 'var(--in-table)', border: '1px solid rgba(167,139,250,0.3)', color: 'white', borderRadius: '3px', padding: '1px 5px', fontWeight: 600 }}>
                                            Prop: {item.descuento_propuesta}%
                                          </span>
                                        )}
                                        {(item.descuento_final || 0) > 0 && (
                                          <span style={{ fontSize: '0.6rem', background: '#d79a00', border: '1px solid rgba(251,191,36,0.35)', color: 'white', borderRadius: '3px', padding: '1px 5px', fontWeight: 700 }}>
                                            Final: {item.descuento_final}%
                                          </span>
                                        )}
                                        {(item.descuento_pac || 0) > 0 && (
                                          <span style={{ fontSize: '0.6rem', background: '#4ade80', border: '1px solid rgba(34,197,94,0.3)', color: 'white', borderRadius: '3px', padding: '1px 5px', fontWeight: 600 }}>
                                            PAC: {item.descuento_pac}%
                                          </span>
                                        )}
                                      </div>

                                      {fechaDespachoItem && (
                                        <div style={{ fontSize: '0.6rem', color: 'var(--text-third)', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                          <Truck size={10} />
                                          <span>Desp: {formatDateTime(fechaDespachoItem)}</span>
                                        </div>
                                      )}
                                    </td>
                                    <td style={{ textAlign: 'right', fontSize: '0.8rem' }}>{formatMoneda(totalCotizadoItem)}</td>
                                    <td style={{ textAlign: 'right', fontSize: '0.8rem' }}>
                                      {item.reng_neto_procesado != null || item.reng_neto_procesado_usd != null
                                        ? formatMoneda(totalProcesadoItem)
                                        : '-'}
                                    </td>
                                    <td style={{ textAlign: 'right', fontSize: '0.8rem' }}>{formatNum(bultosCotizados)}</td>
                                    <td style={{ textAlign: 'right', fontSize: '0.8rem' }}>{formatNum(bultosFacturados)}</td>
                                    <td style={{ fontSize: '0.75rem' }}>{item.uni_venta}</td>
                                    <td style={{ fontSize: '0.75rem', color: 'var(--sd-button-active)' }}>{item.marca}</td>
                                    <td>
                                      <span style={{
                                        padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                                        background: esCompleto ? '#00d26a'
                                          : itemEsSinStock ? 'rgba(239,68,68,0.18)'
                                          : '#fbbf24',
                                        color: esCompleto ? 'white'
                                          : itemEsSinStock ? '#f87171'
                                          : 'white',
                                        whiteSpace: 'nowrap'
                                      }}>
                                        {item.estado_linea || 'PEND'}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="table-footer-pagination" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px', background: 'rgba(0,0,0,0.2)', borderRadius: '0 0 12px 12px' }}>
              <div style={{ display: 'flex', gap: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-third)' }}><List size={16} /><span>{formatNum(totalCount)} pedidos</span></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: '20px' }}><DollarSign size={16} color="#00ff88" /><span>Total USD: {formatUSD(totalUsdGlobal)}</span></div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.6 }}><Database size={14} /><span>DimassiDB Core</span></div>
            </div>
          </>
        )}
      </div>

      {showOrderPanel && (
        <PedidoIndividual
          catalogData={catalogData}
          isAsesor={isAsesor}
          username={username}
          onClose={() => setShowOrderPanel(false)}
          onOrderCreated={() => {
            setShowOrderPanel(false);
            fetchData(currentPage);
          }}
        />
      )}

      <footer className="status-footer">
        {isQuerying ? "SINCRONIZANDO..." : `PEDIDOS: ${formatNum(totalCount)} | PÁGINA ${currentPage} DE ${totalPages}`}
      </footer>
    </motion.div>
  );
};

export default Pedidos;